import { Fragment, useState } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { CgSearch } from 'react-icons/cg';
import { HiFilter } from 'react-icons/hi';
import { RiDeleteBin5Line, RiEyeFill } from 'react-icons/ri';

import Divider, { Orientation } from './Divider';
import Text from './Text';

interface Props {
  title: string;
  searchParams: string[];
  viewResult: () => void;
  deleteAlert: () => void;
}

const Alert = ({ title, searchParams, viewResult, deleteAlert }: Props) => {
  const [showModal, setShowModal] = useState(false);

  function checkos() {
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/windows phone/i.test(userAgent)) {
      return 'Windows Phone';
    }

    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    }

    return 'web';
  }

  const track = (event: string) => {
    // @ts-ignore
    if (window.rudderanalytics?.track) {
      window.rudderanalytics.track(event, {
        platform: checkos(),
      });
    }
  };

  function closeModal() {
    setShowModal(false);
  }

  return (
    <>
      {searchParams && (
        <div className="flex justify-between grow w-[23rem] h-[22rem] mr-4 mb-4 flex-col rounded-lg border border-neutral-300 p-6">
          <div className="flex flex-col ">
            <Text
              as="h2"
              text={'Vous avez créé une alerte avec les critères suivants :'}
              className="text-lg w-full text-neutral-700 font-extrabold"
            />

            {title && (
              <span className="flex justify-center align-middle items-center self-start pt-2 pr-2 font-bold">
                <span>
                  <CgSearch
                    strokeWidth={2}
                    className="text-gray-600 mr-2"
                    size={20}
                  />
                </span>
                Recherche :
                {title && (
                  <span className="self-center font-light pl-1">{`${title}`}</span>
                )}
              </span>
            )}
            <span className="flex align-middle justify-center items-center self-start py-2 font-bold">
              <span>
                <HiFilter
                  strokeWidth={2}
                  className="text-gray-600 mr-2 "
                  size={20}
                />
              </span>
              Filtres :
            </span>
            <div className=" h-24 overflow-x-hidden overflow-y-scroll scrollbar-thumb-neutral-100/10 scrollbar-track-gray-50/10 scrollbar-thin">
              <div className="flex flex-wrap my-1">
                {searchParams.map((filter) => (
                  <span
                    key={''}
                    id="badge-dismiss-default"
                    className="inline-flex items-center py-1 px-2 m-1 text-sm font-medium rounded-md outline outline-1 outline-gray-400 text-gray-800 bg-gray-200/70 whitespace-nowrap"
                    dangerouslySetInnerHTML={{ __html: filter }}
                  ></span>
                ))}
              </div>
            </div>
          </div>
          <div className="justify-end">
            <Divider
              orientation={Orientation.HORIZONTAL}
              className="self-center !mt-2"
            />
            <div className="flex w-full my-2">
              <button
                className="flex justify-center border border-neutral-300 text-neutral-600 font-semibold rounded-lg w-3/4 py-2 mr-4 hover:bg-neutral-100"
                onClick={() => {
                  viewResult();
                  track('Visited saved search Listing');
                }}
              >
                <RiEyeFill
                  size={20}
                  className="self-center mr-6 text-gray-600"
                />
                Voir résultat
              </button>
              <Divider
                orientation={Orientation.VERTICAL}
                className="self-center"
              />

              <button
                className="flex items-center justify-center w-1/4"
                onClick={() => {
                  setShowModal(true);
                }}
              >
                <RiDeleteBin5Line size={26} className="text-primary" />
              </button>
            </div>
          </div>
        </div>
      )}

      <Transition appear show={showModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-white bg-opacity-50 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-[20rem] lg:w-[50rem] border border-secondary/50 h-full lg:h-full mx-auto transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <button
                    onClick={closeModal}
                    type="button"
                    className="absolute top-3 right-2.5 text-gray-400 text-sm p-1.5"
                  >
                    <svg
                      aria-hidden="true"
                      className="w-5 h-5 rounded-full fill-primary self-end cursor-pointer"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <div className="w-full p-3 md:p-14 text-center ">
                    <div className="flex lg:flex-nowrap flex-wrap">
                      <img
                        src="/media/YesOrNo.svg"
                        className=" w-[8rem] h-[8rem] lg:w-[15rem] lg:h-[15rem] mx-auto mb-4 lg:mb-0 lg:mr-8"
                        alt="tayara confirmation illustration: Yes or No"
                      />
                      <div className="flex flex-col justify-evenly">
                        <h3 className="mb-5 text-lg font-bold text-gray-700">
                          Êtes-vous sûr de vouloir supprimer cette alerte ?
                        </h3>
                        <p className="mx-auto mb-5 w-full text-xs lg:text-sm font-normal text-gray-700">
                          La suppression de cette alerte l{`'`}effacera
                          définitivement de votre liste de recherches
                          sauvegardées.
                        </p>
                        <button
                          onClick={() => {
                            deleteAlert();
                            closeModal();
                          }}
                          type="button"
                          className="text-white bg-red-600 font-bold  focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm px-5 py-2.5 mb-3 lg:mb-0 focus:z-10"
                        >
                          Supprimer
                        </button>
                        <button
                          onClick={closeModal}
                          type="button"
                          className="text-gray-700 bg-white  focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Alert;
